// @ts-strict-ignore
import { MultiSelect } from '@usealloy/component-library';
import React, { forwardRef, useContext } from 'react';

import type { Action } from 'flow/store/engine/types';
import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import { createWorkflowHistoryThunk, replaceDefaultWorkflowRoutingThunk } from 'flow/store/redux/thunks';
import {
  selectWorkflowProps,
  selectWorkflowRoutingActions,
} from 'flow/store/redux/selectors';
import { updateAction } from 'flow/store/redux/slice';

import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { UserContext } from '~/dashboard/context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { buildWorkflowRoutingOptions } from './helpers';
import type { WorkflowRoutingOption } from './types';
import SingleValue from './SingleValue';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';


interface WorkflowRoutingProps {
  isDefaultAction?: boolean;
  action: Action;
  dataTestId?: string;
}

const WorkflowRoutingDropdown = forwardRef<any, WorkflowRoutingProps>(({
  isDefaultAction = false,
  action,
  dataTestId,
}, ref) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);
  const { allWorkflows } = useAppSelector(selectWorkflowProps);
  const workflowRoutingActions = useAppSelector(selectWorkflowRoutingActions);
  const isWorkflowHistoryOn = Boolean(featureFlags?.workflowEditorUndoRedo);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const workflowRoutingOptions = React.useMemo(
    () => buildWorkflowRoutingOptions(allWorkflows, workflowRoutingActions),
    [allWorkflows, workflowRoutingActions],
  );
  const selectedWorkflowRoutingOption = workflowRoutingOptions
    .find(workflowRoutingOption => (
      workflowRoutingOption.meta.workflowId === action.workflow.workflowId),
    ) ?? null;

  const handleOnChange = React.useCallback((option: WorkflowRoutingOption) => {
    const replaceWorkflowRouting = () => {
      if (isDefaultAction) {
        dispatch(replaceDefaultWorkflowRoutingThunk(option.meta.workflowId, action.publicId));
        return;
      }

      dispatch(updateAction({
        path: `${action.publicId}.workflow.name`,
        value: option.value,
      }));

      dispatch(updateAction({
        path: `${action.publicId}.workflow.workflowId`,
        value: option.meta.workflowId,
      }));
    };

    dispatch(createWorkflowHistoryThunk(
      replaceWorkflowRouting,
      isWorkflowHistoryOn,
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.action, {
          publicId: action.publicId,
        }),
      },
    ));
  }, [action.publicId, dispatch, isDefaultAction, isWorkflowHistoryOn]);

  return (
    <span
      data-testid={dataTestId}
      data-value={selectedWorkflowRoutingOption?.['data-value'] || ''}
    >
      <MultiSelect
        // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
        {...MULTISELECT_PROPS}
        ref={ref}
        isDisabled={!inEditMode}
        openMenuOnFocus
        customStyleFunctions={{
          ...MULTISELECT_PROPS.customStyleFunctions,
          container: styles => ({
            ...styles,
            margin: '0',
            minWidth: '80',
          }),
        }}
        options={workflowRoutingOptions}
        onChange={handleOnChange}
        selectedOptions={[selectedWorkflowRoutingOption]}
        // FIXME: BUILD-1368
        isOptionDisabled={(o: WorkflowRoutingOption) => o.disabled}
        components={{
          SingleValue,
        }}
      />
    </span>
  );
});

WorkflowRoutingDropdown.displayName = 'WorkflowRoutingDropdown';
export default WorkflowRoutingDropdown;
